import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  db: {
    schema: 'public'
  }
});

// Function to get country from IP
const getCountryFromIP = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.country_name || 'Unknown';
  } catch (error) {
    console.error('Error fetching country:', error);
    return 'Unknown';
  }
};

// Function to update user location
export const updateUserLocation = async (userId) => {
  try {
    const country = await getCountryFromIP();
    if (country === 'Unknown') return;

    const { error } = await supabase
      .from('users')
      .update({ country })
      .eq('id', userId);

    if (error) {
      console.error('Error updating location:', error);
    }
  } catch (error) {
    console.error('Error updating user location:', error);
  }
};

// Function to send mail
export const sendMail = async (senderUsername, recipientUsername, subject, content, backgroundColor, textColorVar, bgColorVar, stamp, existingMailId = null, isHandwritten = false) => {
  try {
    // Clean usernames
    const cleanSenderUsername = senderUsername.split(':')[0];
    const cleanRecipientUsername = recipientUsername.split(':')[0];

    // Get user IDs (check both username and email)
    const { data: senderData, error: senderError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq.${cleanSenderUsername},email.eq.${cleanSenderUsername}`)
      .single();

    if (senderError) throw senderError;

    let recipientId;
    let finalSubject = subject;
    let isUndeliverable = false;

    const { data: recipientData, error: recipientError } = await supabase
      .from('users')
      .select('id, country') // Fetch stored country
      .or(`username.eq.${cleanRecipientUsername},email.eq.${cleanRecipientUsername}`)
      .single();

    if (recipientError || !recipientData) {
      // Recipient not found, return to sender
      recipientId = senderData.id;
      finalSubject = `[Undeliverable] ${subject}`;
      isUndeliverable = true;
    } else {
      recipientId = recipientData.id;
    }

    // Get Sender's Current Country
    const currentSenderCountry = await getCountryFromIP();

    // Calculate Delay Logic
    let delayHours = 24;

    if (isUndeliverable) {
      delayHours = 24;
    } else if (recipientData && recipientData.country && recipientData.country !== currentSenderCountry) {
      delayHours = 72;
    }

    // Calculate Arrival Window (Random 9am - 5pm)
    const now = new Date();
    const minRevealTime = new Date(now.getTime() + delayHours * 60 * 60 * 1000);

    let revealTime = new Date(minRevealTime);
    revealTime.setHours(9, 0, 0, 0); // Start at 9:00 AM

    const randomMinutes = Math.floor(Math.random() * 481);
    revealTime.setMinutes(revealTime.getMinutes() + randomMinutes);

    if (revealTime < minRevealTime) {
      revealTime.setDate(revealTime.getDate() + 1);
    }

    // Bundled Delivery Logic: Synchronize letters to the same recipient on the same day
    // Constraint: Only apply if delivery is more than 24 hours away
    if (revealTime.getTime() - now.getTime() > 24 * 60 * 60 * 1000) {
      try {
        const startOfDay = new Date(revealTime);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(revealTime);
        endOfDay.setHours(23, 59, 59, 999);

        const { data: existingBundledMail } = await supabase
          .from('mails')
          .select('hidden_until')
          .eq('recipient_id', recipientId)
          .gte('hidden_until', startOfDay.toISOString())
          .lte('hidden_until', endOfDay.toISOString())
          .order('sent_at', { ascending: true })
          .limit(1)
          .single();

        if (existingBundledMail && existingBundledMail.hidden_until) {
          revealTime = new Date(existingBundledMail.hidden_until);
          console.log('Bundling delivery with existing mail at:', revealTime);
        }
      } catch (err) {
        // Silently fail if no bundled mail found or query fails
        console.log('No bundled mail found or bundling check failed:', err.message);
      }
    }

    let mailData;
    let mailError;

    if (existingMailId) {
      // Update existing draft row instead of creating a new one
      const { data: updatedData, error: updateError } = await supabase
        .from('mails')
        .update({
          recipient_id: recipientId,
          subject: finalSubject,
          content,
          country: currentSenderCountry,
          background_color: backgroundColor,
          text_color_var: textColorVar,
          bg_color_var: bgColorVar,
          stamp,
          type: 'sent', // Initially 'sent' for everyone
          is_handwritten: isHandwritten,
          hidden_until: revealTime.toISOString(),
          recipient_folder: 'Hidden'
        })
        .eq('id', existingMailId)
        .select()
        .single();

      mailData = updatedData;
      mailError = updateError;
    } else {
      // Insert new mail if no existing ID provided
      const { data: insertedData, error: insertError } = await supabase
        .from('mails')
        .insert({
          sender_id: senderData.id,
          recipient_id: recipientId,
          subject: finalSubject,
          content,
          country: currentSenderCountry,
          background_color: backgroundColor,
          text_color_var: textColorVar,
          bg_color_var: bgColorVar,
          stamp,
          type: 'sent', // Default to sent
          is_handwritten: isHandwritten,
          hidden_until: revealTime.toISOString(),
          recipient_folder: 'Hidden'
        })
        .select()
        .single();

      mailData = insertedData;
      mailError = insertError;
    }

    if (mailError) throw mailError;

    // Determine initial recipient_folder (default 'Inbox', will be updated to 'Hidden' if delayed below)
    // Actually, if we are putting it into 'Hidden' immediately below, we can set it there.
    // Let's set default to 'Inbox' here if not hidden, but since logic below puts it in hidden...
    // The logic below puts it in 'Hidden' folder unconditionally for "received" type?
    // Wait, the code says:
    // // Add to recipient's Hidden folder
    // It seems ALL new mails go to Hidden first?
    // Let's check lines 124+. Yes, it gets Hidden folder.
    // So initial state is 'Hidden'.
    // We can update it after insertion or add it to insert if we want.
    // Adding to insert is cleaner but `mails` insert is on line 105.
    // Let's add it to the insert on line 117.

    // Add to recipient's Hidden folder
    const { data: hiddenFolder, error: folderError } = await supabase
      .from('folders')
      .select('id')
      .eq('user_id', recipientId)
      .eq('name', 'Hidden')
      .single();

    if (folderError) throw folderError;

    const { error: linkError } = await supabase
      .from('folder_mails')
      .insert({
        folder_id: hiddenFolder.id,
        mail_id: mailData.id
      });

    if (linkError) throw linkError;

    // Update mail with recipient_folder = 'Hidden'
    const { error: hiddenUpdateError } = await supabase
      .from('mails')
      .update({ recipient_folder: 'Hidden' })
      .eq('id', mailData.id);

    if (hiddenUpdateError) {
      console.warn('Error updating recipient_folder to Hidden:', hiddenUpdateError);
      // Non-fatal, continue
    }



    return mailData;
  } catch (error) {
    console.error('Error sending mail:', error);
    throw error;
  }
};

// Function to get mails for user
export const getMailsForUser = async (username) => {
  try {
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    const { data: mails, error: mailsError } = await supabase
      .from('mails')
      .select(`
        *,
        sender:users!sender_id(username),
        recipient:users!recipient_id(username)
      `)
      .or(`sender_id.eq.${userData.id},recipient_id.eq.${userData.id}`)
      .order('sent_at', { ascending: false });

    if (mailsError) throw mailsError;

    // Transform data to match local format
    return mails.map(mail => {
      let displayType = mail.type || (mail.sender_id === userData.id ? 'sent' : 'received');

      // Drafts should stay drafts
      if (displayType === 'draft') {
        // Leave it as is
      } else {
        // Logic for sent/received transition
        if (mail.sender_id === userData.id) {
          displayType = 'sent';
        } else if (mail.recipient_id === userData.id) {
          const isRevealed = mail.hidden_until && new Date(mail.hidden_until) <= new Date();
          displayType = isRevealed ? 'received' : 'sent';
        }
      }

      return {
        id: mail.id,
        sender: mail.sender.username,
        recipient: mail.recipient ? mail.recipient.username : '',
        subject: mail.subject,
        content: mail.content,
        sentAt: mail.sent_at,
        country: mail.country,
        type: displayType,
        opened: mail.opened,
        backgroundColor: mail.background_color,
        textColorVar: mail.text_color_var,
        bgColorVar: mail.bg_color_var,
        stamp: mail.stamp,
        isHandwritten: mail.is_handwritten,
        hiddenUntil: mail.hidden_until
      };
    });
  } catch (error) {
    console.error('Error getting mails:', error);
    return [];
  }
};

// Function to get folders for user
export const getFoldersForUser = async (username) => {
  try {
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    const { data: folders, error: foldersError } = await supabase
      .from('folders')
      .select(`
        *,
        folder_mails(mail_id)
      `)
      .eq('user_id', userData.id)
      .eq('deleted', false);

    if (foldersError) throw foldersError;

    // Transform data to match local format
    return folders.map(folder => ({
      id: folder.id,
      name: folder.name,
      mailIds: folder.folder_mails ? folder.folder_mails.map(fm => fm.mail_id) : []
    }));
  } catch (error) {
    console.error('Error getting folders:', error);
    return [];
  }
};

// Function to get deleted folders for user
export const getDeletedFoldersForUser = async (username) => {
  try {
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    const { data: folders, error: foldersError } = await supabase
      .from('folders')
      .select(`
        *,
        folder_mails(mail_id)
      `)
      .eq('user_id', userData.id)
      .eq('deleted', true);

    if (foldersError) throw foldersError;

    // Transform data to match local format
    return folders.map(folder => ({
      id: folder.id,
      name: folder.name,
      mailIds: folder.folder_mails ? folder.folder_mails.map(fm => fm.mail_id) : []
    }));
  } catch (error) {
    console.error('Error getting deleted folders:', error);
    return [];
  }
};

// Function to create a new folder
export const createFolder = async (username, folderName) => {
  try {
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    const { data: folderData, error: folderError } = await supabase
      .from('folders')
      .insert({
        user_id: userData.id,
        name: folderName
      })
      .select()
      .single();

    if (folderError) throw folderError;

    return folderData;
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

// Function to move mail to folder
export const moveMailToFolder = async (mailId, folderId) => {
  try {
    console.log('Moving mail', mailId, 'to folder', folderId);

    // First, remove the mail from all folders belonging to the user who owns this folder
    const { data: folderData, error: fError } = await supabase
      .from('folders')
      .select('user_id')
      .eq('id', folderId)
      .single();

    if (fError) throw fError;

    const { data: userFolders } = await supabase
      .from('folders')
      .select('id')
      .eq('user_id', folderData.user_id);

    const folderIds = userFolders.map(f => f.id);

    const { error: deleteError } = await supabase
      .from('folder_mails')
      .delete()
      .eq('mail_id', mailId)
      .in('folder_id', folderIds);

    if (deleteError) {
      console.error('Delete error:', deleteError);
      throw deleteError;
    }

    // Then add it to the new folder
    const { error: insertError } = await supabase
      .from('folder_mails')
      .insert({
        folder_id: folderId,
        mail_id: mailId
      });

    if (insertError) {
      console.error('Insert error:', insertError);
      throw insertError;
    }

    // Update recipient_folder column if applicable
    const { data: fInfo } = await supabase.from('folders').select('user_id, name').eq('id', folderId).single();
    if (fInfo) {
      const { data: mailData } = await supabase.from('mails').select('recipient_id').eq('id', mailId).single();
      if (mailData && mailData.recipient_id === fInfo.user_id) {
        await supabase.from('mails').update({ recipient_folder: fInfo.name }).eq('id', mailId);
      }
    }

    return true;
  } catch (error) {
    console.error('Error moving mail to folder:', error);
    throw error;
  }
};



// Function to upload hand-drawn images
export const uploadDrawing = async (userId, base64Data, filename) => {
  try {
    // Convert Base64 skip prefix
    const base64Content = base64Data.split(',')[1];
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    const filePath = `${userId}/${Date.now()}_${filename}.png`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('drawings')
      .upload(filePath, blob, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('drawings')
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading drawing:', error);
    throw error;
  }
};


// Function to save draft
export const saveDraft = async (username, recipientUsername, subject, content, backgroundColor, textColorVar, bgColorVar, isHandwritten = false) => {
  try {
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    // Get recipient ID if provided
    let recipientId = null;
    if (recipientUsername) {
      // Clean recipient username
      const cleanRecipientUsername = recipientUsername.split(':')[0];

      const { data: recipientData, error: recipientError } = await supabase
        .from('users')
        .select('id')
        .or(`username.eq.${cleanRecipientUsername},email.eq.${cleanRecipientUsername}`)
        .single();

      if (!recipientError) {
        recipientId = recipientData.id;
      }
    }

    // Insert draft mail
    const { data: mailData, error: mailError } = await supabase
      .from('mails')
      .insert({
        sender_id: userData.id,
        recipient_id: recipientId,
        subject,
        content,
        background_color: backgroundColor,
        text_color_var: textColorVar,
        bg_color_var: bgColorVar,
        type: 'draft',
        is_handwritten: isHandwritten
      })
      .select()
      .single();

    if (mailError) throw mailError;

    // Add to user's Drafts folder (create if it doesn't exist)
    let draftsFolder;
    const { data: existingFolder, error: folderError } = await supabase
      .from('folders')
      .select('id')
      .eq('user_id', userData.id)
      .eq('name', 'Drafts')
      .single();

    if (folderError && folderError.code === 'PGRST116') {
      // Folder doesn't exist, create it
      const { data: nFolder, error: crError } = await supabase
        .from('folders')
        .insert({
          user_id: userData.id,
          name: 'Drafts'
        })
        .select()
        .single();

      if (crError) throw crError;
      draftsFolder = nFolder;
    } else if (folderError) {
      throw folderError;
    } else {
      draftsFolder = existingFolder;
    }

    const { error: linkError } = await supabase
      .from('folder_mails')
      .insert({
        folder_id: draftsFolder.id,
        mail_id: mailData.id
      });

    if (linkError) throw linkError;

    return mailData;
  } catch (error) {
    console.error('Error saving draft:', error);
    throw error;
  }
};

// Function to update draft
export const updateDraft = async (mailId, recipientUsername, subject, content, backgroundColor, textColorVar, bgColorVar, isHandwritten = false) => {
  try {
    // Get recipient ID if provided
    let recipientId = null;
    if (recipientUsername) {
      // Clean recipient username
      const cleanRecipientUsername = recipientUsername.split(':')[0];

      const { data: recipientData, error: recipientError } = await supabase
        .from('users')
        .select('id')
        .or(`username.eq.${cleanRecipientUsername},email.eq.${cleanRecipientUsername}`)
        .single();

      if (!recipientError) {
        recipientId = recipientData.id;
      }
    }

    // Update draft mail
    const { data: mailData, error: mailError } = await supabase
      .from('mails')
      .update({
        recipient_id: recipientId,
        subject,
        content,
        background_color: backgroundColor,
        text_color_var: textColorVar,
        bg_color_var: bgColorVar,
        is_handwritten: isHandwritten
      })
      .eq('id', mailId)
      .select()
      .single();

    if (mailError) throw mailError;

    return mailData;
  } catch (error) {
    console.error('Error updating draft:', error);
    throw error;
  }
};

// Function to remove mail from folder
export const removeMailFromFolder = async (mailId, folderId) => {
  try {
    const { error } = await supabase
      .from('folder_mails')
      .delete()
      .eq('mail_id', mailId)
      .eq('folder_id', folderId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error removing mail from folder:', error);
    throw error;
  }
};

// Function to update draft type to sent
export const updateDraftToSent = async (mailId) => {
  try {
    const { data: mailData, error: mailError } = await supabase
      .from('mails')
      .update({
        type: 'sent'
      })
      .eq('id', mailId)
      .select()
      .single();

    if (mailError) throw mailError;

    return mailData;
  } catch (error) {
    console.error('Error updating draft to sent:', error);
    throw error;
  }
};

// Function to move mail to folder by folder name
export const moveMailToFolderByName = async (mailId, folderName, username) => {
  try {
    // Get user ID
    // Clean username
    const cleanUsername = username.split(':')[0];

    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .or(`username.eq."${cleanUsername}",email.eq."${cleanUsername}"`)
      .single();

    if (userError) throw userError;

    // Get folder ID by name
    const { data: folderData, error: folderError } = await supabase
      .from('folders')
      .select('id')
      .eq('user_id', userData.id)
      .eq('name', folderName)
      .single();

    if (folderError) throw folderError;

    // Move mail to folder
    await moveMailToFolder(mailId, folderData.id);

    // Update recipient_folder directly here since we have the data
    const { data: mailData } = await supabase.from('mails').select('recipient_id').eq('id', mailId).single();
    if (mailData && mailData.recipient_id === userData.id) {
      await supabase.from('mails').update({ recipient_folder: folderName }).eq('id', mailId);
    }

    return true;
  } catch (error) {
    console.error('Error moving mail to folder by name:', error);
    throw error;
  }
};

// Function to delete mail from Supabase
export const deleteMailFromSupabase = async (mailId) => {
  try {
    // First, remove from all folders
    const { error: folderError } = await supabase
      .from('folder_mails')
      .delete()
      .eq('mail_id', mailId);

    if (folderError) throw folderError;

    // Then delete the mail
    const { error: mailError } = await supabase
      .from('mails')
      .delete()
      .eq('id', mailId);

    if (mailError) throw mailError;

    return true;
  } catch (error) {
    console.error('Error deleting mail from Supabase:', error);
    throw error;
  }
};

// Function to soft delete a folder (move to Bin)
export const softDeleteFolder = async (folderId) => {
  try {
    // Update folder to deleted = true
    const { error: updateError } = await supabase
      .from('folders')
      .update({ deleted: true })
      .eq('id', folderId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error soft deleting folder:', error);
    throw error;
  }
};

// Function to restore a deleted folder
export const restoreFolderSupabase = async (folderId) => {
  try {
    // Update folder to deleted = false
    const { error: updateError } = await supabase
      .from('folders')
      .update({ deleted: false })
      .eq('id', folderId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error restoring folder:', error);
    throw error;
  }
};

// Function to permanently delete a folder
export const deleteFolderFromSupabase = async (folderId) => {
  try {
    // First, remove all mails from this folder
    const { error: folderMailsError } = await supabase
      .from('folder_mails')
      .delete()
      .eq('folder_id', folderId);

    if (folderMailsError) throw folderMailsError;

    // Then delete the folder
    const { error: folderError } = await supabase
      .from('folders')
      .delete()
      .eq('id', folderId);

    if (folderError) throw folderError;

    return true;
  } catch (error) {
    console.error('Error deleting folder from Supabase:', error);
    throw error;
  }
};

// Function to initiate signup verification
export const initiateSignupVerification = async (name, username, email, password) => {
  try {
    // Check if username already exists in users
    const { data: existingUsername } = await supabase
      .from('users')
      .select('id')
      .eq('username', username)
      .limit(1);

    if (existingUsername && existingUsername.length > 0) {
      throw new Error('Username is already taken. Please choose a different username.');
    }

    // Sign up using Supabase Auth
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          username: username
        },
        emailRedirectTo: `${window.location.origin}/verified`
      }
    });

    if (error) throw error;

    return { user: data.user, session: data.session };
  } catch (error) {
    console.error('Error initiating signup verification:', error);
    throw error;
  }
};

// Function to verify user with token (using Supabase Auth redirect)
export const verifyUser = async (token) => {
  // In Supabase Auth, clicking the link verifies the user.
  // We can check if the user is now authenticated.
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;

    if (session) {
      return session.user;
    }

    // If no session yet, we might be in the middle of a redirect or token exchange
    // The Verification component will handle the state.
    return null;
  } catch (error) {
    console.error('Error in verifyUser:', error);
    throw error;
  }
};
// Function to update tutorial status
export const updateTutorialStatus = async (userId) => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ has_seen_tutorial: true })
      .eq('id', userId);

    if (error) {
      console.error('Error updating tutorial status:', error);
      throw error;
    }
    return true;
  } catch (error) {
    console.error('Error in updateTutorialStatus:', error);
    throw error;
  }
};
